import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ShoppingCart, Loader2, AlertCircle, ArrowLeft, Lock, CheckCircle, Minus, Plus, Trash2, UtensilsCrossed } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const fmt = (n) => `$${(n || 0).toFixed(2)}`;

function isLight(hex = '') {
  const h = (hex || '').replace('#', '');
  if (h.length < 6) return false;
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) > 160;
}
function darkenHex(hex = '', amount = 55) {
  const h = (hex || '').replace('#', '');
  if (h.length < 6) return hex;
  return '#' + [0, 2, 4].map(i =>
    Math.max(0, parseInt(h.substring(i, i + 2), 16) - amount).toString(16).padStart(2, '0')
  ).join('');
}

export const CustomerCart = () => {
  const { deviceId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [cart, setCart] = useState({ items: [], totalAmount: 0 });
  const [pinRequired, setPinRequired] = useState(false);
  const [allocationPin, setAllocationPin] = useState('');
  const [enteredPin, setEnteredPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [pinVerified, setPinVerified] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [remarkDrafts, setRemarkDrafts] = useState({});

  useEffect(() => { fetchData(); }, [deviceId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [deviceRes, cartRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/device/${deviceId}/data`),
        axios.get(`${BACKEND_URL}/api/cart/${deviceId}`),
      ]);
      if (deviceRes.data.code !== 200) {
        setError(deviceRes.data.message || 'No allocation found'); return;
      }
      const dd = deviceRes.data.data;
      setData(dd);
      if (dd.allocation?.pinEnabled) { setPinRequired(true); setAllocationPin(dd.allocation.pin || ''); }
      if (cartRes.data.code === 200 && cartRes.data.data) setCart(cartRes.data.data);
    } catch { setError('Failed to load cart. Please try again.'); }
    finally { setLoading(false); }
  };

  const updateCartItem = async (menuItemId, qty) => {
    try {
      const res = await axios.put(`${BACKEND_URL}/api/cart/${deviceId}/update`, { menuItemId, quantity: qty });
      if (res.data.code === 200) setCart(res.data.data);
    } catch (_) {}
  };

  const removeFromCart = async (menuItemId) => {
    try {
      const res = await axios.delete(`${BACKEND_URL}/api/cart/${deviceId}/remove/${menuItemId}`);
      if (res.data.code === 200) setCart(res.data.data);
    } catch (_) {}
  };

  const saveItemNotes = async (menuItemId, notes) => {
    try {
      const res = await axios.put(`${BACKEND_URL}/api/cart/${deviceId}/update-notes`, { menuItemId, notes });
      if (res.data.code === 200) setCart(res.data.data);
    } catch (_) {}
  };

  const handlePlaceOrder = async () => {
    if (pinRequired && !pinVerified) {
      if (enteredPin.length !== 4) { setPinError('Please enter the 4-digit PIN.'); return; }
      if (enteredPin !== allocationPin) { setPinError('Invalid PIN. Please try again.'); return; }
      setPinVerified(true); setPinError('');
    }
    setPlacingOrder(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/orders/place`, { deviceId, pin: pinRequired ? enteredPin : undefined });
      if (res.data.code === 200) setOrderPlaced(true);
      else setPinError(res.data.message || 'Failed to place order.');
    } catch { setPinError('Something went wrong. Please try again.'); }
    finally { setPlacingOrder(false); }
  };

  /* ── theme ── */
  const themeColor = data?.property?.themeColor || '#92400e';
  const darkColor = darkenHex(themeColor, 55);
  const textOnTheme = isLight(themeColor) ? '#1c1917' : '#ffffff';
  const mutedOnTheme = isLight(themeColor) ? 'rgba(28,25,23,0.65)' : 'rgba(255,255,255,0.65)';
  const logoUrl = data?.property?.logoUrl || '';

  /* ── loading ── */
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: `linear-gradient(160deg, ${themeColor} 0%, ${darkenHex(themeColor, 55)} 100%)` }}>
      <div className="flex flex-col items-center gap-4">
        <div className="w-14 h-14 rounded-full border-4 border-white/30 border-t-white animate-spin" />
        <p className="text-white/70 text-sm font-medium">Loading cart…</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center p-6">
      <div className="bg-white rounded-3xl shadow-xl p-8 max-w-sm w-full text-center">
        <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <AlertCircle className="w-8 h-8 text-rose-500" />
        </div>
        <h2 className="text-xl font-bold text-stone-800 mb-2">Oops!</h2>
        <p className="text-stone-500 text-sm">{error}</p>
      </div>
    </div>
  );

  /* ── order placed success ── */
  if (orderPlaced) return (
    <div className="min-h-screen bg-stone-50 flex flex-col">
      {/* Logo strip */}
      <div className="w-full bg-white shadow-sm flex items-center justify-center px-6 py-3" style={{ minHeight: '56px' }}>
        {logoUrl
          ? <img src={logoUrl} alt="logo" className="h-10 w-auto max-w-[180px] object-contain" />
          : <span className="text-base font-bold text-stone-700">{data?.property?.name || 'SmartFlags'}</span>}
      </div>
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="bg-white rounded-3xl shadow-xl p-8 max-w-sm w-full text-center">
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-5"
            style={{ backgroundColor: `${themeColor}20` }}>
            <CheckCircle className="w-10 h-10" style={{ color: themeColor }} />
          </div>
          <h2 className="text-2xl font-bold text-stone-800 mb-2">Order Placed!</h2>
          <p className="text-stone-500 text-sm mb-6">Your order has been placed. Our staff will serve you shortly.</p>
          <div className="rounded-2xl p-4 mb-6" style={{ backgroundColor: `${themeColor}12` }}>
            <p className="text-xs text-stone-400 mb-1">Order Total</p>
            <p className="text-3xl font-extrabold" style={{ color: themeColor }}>{fmt(cart.totalAmount)}</p>
          </div>
          <button onClick={() => navigate(`/customer/menu/${deviceId}`)}
            className="w-full py-3.5 rounded-2xl text-white font-bold text-base transition-all active:scale-[0.98]"
            style={{ backgroundColor: themeColor }}>
            Back to Menu
          </button>
        </div>
      </div>
    </div>
  );

  /* ── main cart ── */
  return (
    <div className="min-h-screen bg-stone-100 pb-36">

      {/* ── Full-width logo strip ── */}
      <div className="w-full bg-white shadow-sm flex items-center justify-center px-6 py-3" style={{ minHeight: '56px' }}>
        {logoUrl
          ? <img src={logoUrl} alt="logo" className="h-10 w-auto max-w-[180px] object-contain" data-testid="property-logo" />
          : <span className="text-base font-bold text-stone-700">{data?.property?.name || 'SmartFlags'}</span>}
      </div>

      {/* ── Theme-coloured header ── */}
      <div className="relative overflow-hidden"
        style={{ background: `linear-gradient(160deg, ${themeColor} 0%, ${darkColor} 100%)` }}>
        <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-white/5" />
        <div className="relative z-10 max-w-2xl mx-auto px-5 pt-5 pb-5 flex items-center gap-4">
          <button onClick={() => navigate(`/customer/menu/${deviceId}`)}
            className="w-9 h-9 rounded-full flex items-center justify-center transition-all active:scale-90 shrink-0"
            style={{ background: 'rgba(255,255,255,0.15)' }}
            data-testid="cart-back-btn">
            <ArrowLeft className="w-5 h-5" style={{ color: textOnTheme }} />
          </button>
          <div>
            <h1 className="text-xl font-bold" style={{ color: textOnTheme }}>Your Cart</h1>
            {data?.allocation && (
              <p className="text-sm" style={{ color: mutedOnTheme }}>
                {data.allocation.guestName} · Room {data.allocation.roomNumber}
              </p>
            )}
          </div>
          {cart.items?.length > 0 && (
            <div className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-full"
              style={{ background: 'rgba(255,255,255,0.15)' }}>
              <ShoppingCart className="w-4 h-4" style={{ color: textOnTheme }} />
              <span className="text-sm font-bold" style={{ color: textOnTheme }}>{cart.items.length}</span>
            </div>
          )}
        </div>
        <svg viewBox="0 0 1440 20" className="w-full -mb-px" preserveAspectRatio="none" style={{ height: '20px', fill: '#e7e5e4' }}>
          <path d="M0,10 C360,20 1080,0 1440,10 L1440,20 L0,20 Z" />
        </svg>
      </div>

      {/* ── Content ── */}
      <div className="max-w-2xl mx-auto px-4 pt-4 space-y-3">
        {!cart.items?.length ? (
          /* Empty cart */
          <div className="bg-white rounded-2xl shadow-sm p-10 text-center mt-4">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: `${themeColor}15` }}>
              <ShoppingCart className="w-8 h-8" style={{ color: themeColor }} />
            </div>
            <h2 className="text-lg font-bold text-stone-800 mb-2">Your cart is empty</h2>
            <p className="text-stone-500 text-sm mb-6">Add some items from the menu to get started.</p>
            <button onClick={() => navigate(`/customer/menu/${deviceId}`)}
              className="px-6 py-3 rounded-2xl text-white font-semibold text-sm transition-all active:scale-[0.98]"
              style={{ backgroundColor: themeColor }}>
              Browse Menu
            </button>
          </div>
        ) : (
          <>
            {/* Items card */}
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-stone-100 flex items-center justify-between">
                <h2 className="font-bold text-stone-800">Order Items</h2>
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full text-white" style={{ backgroundColor: themeColor }}>
                  {cart.items.length} item{cart.items.length > 1 ? 's' : ''}
                </span>
              </div>
              <div className="divide-y divide-stone-100">
                {cart.items.map((item, idx) => (
                  <div key={idx} className="p-4" data-testid={`cart-item-${item.menuItemId}`}>
                    <div className="flex items-start gap-3">
                      {/* Icon placeholder */}
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                        style={{ backgroundColor: `${themeColor}15` }}>
                        <UtensilsCrossed className="w-4 h-4" style={{ color: themeColor }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="font-semibold text-stone-800 text-sm leading-snug">{item.menuItemName}</h3>
                          <p className="font-bold text-stone-800 text-sm shrink-0">{fmt(item.price * item.quantity)}</p>
                        </div>
                        <p className="text-xs text-stone-400 mt-0.5">{fmt(item.price)} each</p>
                      </div>
                    </div>

                    {/* Qty + Remove */}
                    <div className="flex items-center gap-2 mt-3 ml-13 pl-[52px]">
                      <div className="flex items-center bg-stone-100 rounded-xl overflow-hidden">
                        <button onClick={() => updateCartItem(item.menuItemId, item.quantity - 1)}
                          className="w-8 h-8 flex items-center justify-center hover:bg-stone-200 transition-colors">
                          <Minus className="w-3 h-3 text-stone-600" />
                        </button>
                        <span className="w-8 text-center text-sm font-bold text-stone-700">{item.quantity}</span>
                        <button onClick={() => updateCartItem(item.menuItemId, item.quantity + 1)}
                          className="w-8 h-8 flex items-center justify-center hover:bg-stone-200 transition-colors">
                          <Plus className="w-3 h-3 text-stone-600" />
                        </button>
                      </div>
                      <button onClick={() => removeFromCart(item.menuItemId)}
                        className="w-8 h-8 flex items-center justify-center text-rose-400 hover:bg-rose-50 rounded-xl transition-colors ml-auto">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Notes */}
                    <div className="mt-3 ml-[52px]" data-testid={`remark-editor-${item.menuItemId}`}>
                      <textarea
                        value={remarkDrafts[item.menuItemId] ?? item.notes ?? ''}
                        onChange={(e) => setRemarkDrafts(p => ({ ...p, [item.menuItemId]: e.target.value }))}
                        onBlur={() => {
                          const draft = remarkDrafts[item.menuItemId];
                          if (draft !== undefined && draft !== (item.notes || '')) saveItemNotes(item.menuItemId, draft);
                        }}
                        placeholder="Add a note (e.g. no ice, extra spicy...)"
                        className="w-full text-xs border border-stone-200 rounded-xl px-3 py-2 focus:outline-none resize-none text-stone-600 placeholder-stone-300"
                        style={{ '--tw-ring-color': themeColor }}
                        rows={1}
                        data-testid={`remark-input-${item.menuItemId}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Summary card */}
            <div className="bg-white rounded-2xl shadow-sm p-5">
              <h2 className="font-bold text-stone-800 mb-4">Order Summary</h2>
              <div className="space-y-2.5 text-sm">
                <div className="flex justify-between text-stone-500">
                  <span>Subtotal</span><span>{fmt(cart.totalAmount)}</span>
                </div>
                <div className="flex justify-between text-stone-500">
                  <span>Service Charge</span><span>$0.00</span>
                </div>
                <div className="border-t border-stone-100 pt-3 flex justify-between">
                  <span className="font-bold text-stone-800 text-base">Total</span>
                  <span className="font-extrabold text-lg" style={{ color: themeColor }}>{fmt(cart.totalAmount)}</span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── Sticky bottom: PIN + Place Order ── */}
      {cart.items?.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-t border-stone-100 px-4 py-4 z-20 shadow-2xl">
          <div className="max-w-2xl mx-auto space-y-3">
            {pinRequired && !pinVerified && (
              <div className="flex items-center gap-3 bg-stone-50 rounded-2xl px-4 py-2">
                <Lock className="w-5 h-5 shrink-0" style={{ color: themeColor }} />
                <input
                  type="text" inputMode="numeric" pattern="[0-9]*" maxLength={4}
                  value={enteredPin}
                  onChange={(e) => { setEnteredPin(e.target.value.replace(/\D/g, '').slice(0, 4)); setPinError(''); }}
                  placeholder="Enter 4-digit PIN"
                  className="flex-1 bg-transparent text-center text-lg tracking-[0.4em] font-mono font-bold focus:outline-none text-stone-700 placeholder-stone-300"
                  data-testid="pin-input"
                />
              </div>
            )}
            {pinError && (
              <p className="text-rose-500 text-sm text-center font-medium" data-testid="pin-error">{pinError}</p>
            )}
            <button
              onClick={handlePlaceOrder} disabled={placingOrder}
              className="w-full py-4 rounded-2xl text-white font-bold text-base flex items-center justify-center gap-2 transition-all active:scale-[0.98] disabled:opacity-60"
              style={{ backgroundColor: themeColor, boxShadow: `0 6px 20px ${themeColor}55` }}
              data-testid="place-order-btn">
              {placingOrder
                ? <><Loader2 className="w-5 h-5 animate-spin" /> Placing Order…</>
                : `Place Order · ${fmt(cart.totalAmount)}`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
